import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { GetProjectsQueryParameters } from '../../../../../typings/api/skymap/rest/v1/project';
import { SkyMapAxiosServiceFactory } from '../../../services/axios/skymap-axios-service-factory';
import { isDefined } from '../../../utils/variables';
import { useErrorHandling } from '../../hooks/use-error-handling';
import { useOptionalUserState } from '../../state/user-state/user-state';

type Params = {
  limit: number;
  offset: number;
  orderBy?: GetProjectsQueryParameters['orderBy'];
  lifecycleStatus?: GetProjectsQueryParameters['lifecycleStatus'];
  name?: string;
  companyIds?: string[];
  enabled?: boolean;
};

async function fetchProjects(params: Params) {
  const result = await SkyMapAxiosServiceFactory.instance.createProjectServiceV1().getProjects({
    query: {
      include: ['projects.company', 'projects.latestArchiveJob', 'projects.latestRestoreJob'],
      limit: params.limit,
      offset: params.offset,
      orderBy: params.orderBy,
      lifecycleStatus: params.lifecycleStatus,
      name: params.name,
      companyIds: params.companyIds,
    },
  });
  return result;
}

export function useProjectsQuery(params: Params) {
  const { handleError, clearErrors, buildErrorList } = useErrorHandling();
  const userState = useOptionalUserState();
  const userId = userState?.user?.id ?? '';

  const { data, refetch, error, isLoading } = useQuery({
    queryKey: [
      'getProjects',
      userId,
      params.limit,
      params.offset,
      params.orderBy,
      params.lifecycleStatus,
      params.name,
      params.companyIds,
    ],
    queryFn: () => fetchProjects(params),
    enabled: params.enabled ?? true,
    networkMode: 'always',
    retry: false,
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (isDefined(error)) {
      handleError(error, { retryCallback: refetch });
    } else {
      clearErrors();
    }
  }, [handleError, clearErrors, error, refetch]);

  const errorList = useMemo(
    () =>
      buildErrorList({
        infoBoxProps: {
          width: 'fit-content',
        },
      }),
    [buildErrorList],
  );

  const projects = Array.isArray(data?.data) ? data.data : undefined;

  return {
    projects,
    pagination: data?.meta?.pagination,
    errorList,
    isLoading,
    refetch,
  };
}
